import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { KatButton, KatDropdown, KatInput, KatModal } from '@amzn/katal-react';
import { DeviceFamily, PageType, Placement } from '@amzn/fire-tv-campaign-automation-service';
import { DevicePreviewConstants } from 'src/constants';
import { PAGE_TYPE_CONFIG } from 'src/configs/pageTypeConfiguration';
import { getPageIdsForPageType, getSlotOptionsForPageId } from 'src/configs/campaignMetadataConfig';
import { PropUtils } from 'src/utils/PropUtils';
import styles from './DevicePreviewModal.module.css';

export interface StartPreviewParams {
  deviceId: string;
  customerId: string;
  creativeId: string;
  durationInSeconds: number;
  pageType: string;
  pageId: string;
  slot: string;
}

export interface DeviceOption {
  name: string;
  value: string;
  customerId?: string;
}

interface DevicePreviewDefaults {
  pageType?: string;
  pageId?: string;
  slot?: string;
}

interface DevicePreviewModalProps {
  isVisible: boolean;
  onClose: () => void;
  onStartPreview: (params: StartPreviewParams) => Promise<void>;
  isLoading: boolean;
  deviceFamily: DeviceFamily;
  deviceOptions?: DeviceOption[];
  defaults?: DevicePreviewDefaults;
}

const EMPTY_DEFAULTS: DevicePreviewDefaults = {};

const { LABELS, VALIDATION, DEFAULT_DURATION_SECONDS, DURATION_OPTIONS } = DevicePreviewConstants;

const computeDefaultFormValues = (
  defaults: DevicePreviewDefaults,
  defaultPageType: string,
): { pageType: string; pageId: string; slot: string } => {
  const pageType = defaults.pageType ?? defaultPageType;
  const ids = pageType ? getPageIdsForPageType(pageType as PageType) : [];
  const pageId = defaults.pageId ?? ids[0]?.value ?? '';
  const slots = pageId ? getSlotOptionsForPageId(pageId) : [];
  const slot = defaults.slot ?? slots[0]?.value ?? '';
  return { pageType, pageId, slot };
};

export const DevicePreviewModal: FC<DevicePreviewModalProps> = ({
  isVisible,
  onClose,
  onStartPreview,
  isLoading,
  deviceFamily,
  deviceOptions = [],
  defaults = EMPTY_DEFAULTS,
}) => {
  const defaultDeviceId = useMemo(
    () => (deviceOptions.length > 0 ? deviceOptions[0].value : LABELS.PHYSICAL_DEVICE),
    [deviceOptions],
  );

  const defaultPageType = useMemo(() => {
    const types = PAGE_TYPE_CONFIG[deviceFamily]?.[Placement.FEATURE_ROTATOR] ?? [];
    return types[0] ?? '';
  }, [deviceFamily]);

  const [deviceId, setDeviceId] = useState(defaultDeviceId);
  const [customerId, setCustomerId] = useState(
    () => deviceOptions.find((d) => d.value === defaultDeviceId)?.customerId ?? '',
  );
  const [creativeId, setCreativeId] = useState('');
  const [duration, setDuration] = useState(DEFAULT_DURATION_SECONDS);
  const initForm = computeDefaultFormValues(defaults, defaultPageType);
  const [pageType, setPageType] = useState(initForm.pageType);
  const [pageId, setPageId] = useState(initForm.pageId);
  const [slot, setSlot] = useState(initForm.slot);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const isPhysicalDevice = deviceId === LABELS.PHYSICAL_DEVICE;

  const allDeviceOptions = useMemo(
    () => [
      { name: LABELS.PHYSICAL_DEVICE, value: LABELS.PHYSICAL_DEVICE },
      ...(deviceOptions.length > 0
        ? deviceOptions
        : [{ name: LABELS.NO_DEVICE_CONNECTED, value: '', disabled: true }]),
    ],
    [deviceOptions],
  );

  const pageTypeOptions = useMemo(() => {
    const types = PAGE_TYPE_CONFIG[deviceFamily]?.[Placement.FEATURE_ROTATOR] ?? [];
    return PropUtils.createOptionsFromEnumValues(types);
  }, [deviceFamily]);

  // Cascade: PageType → PageIds
  const pageIdOptions = useMemo(
    () => (pageType ? getPageIdsForPageType(pageType as PageType) : []),
    [pageType],
  );

  // Cascade: PageId → Slots
  const slotOptions = useMemo(() => (pageId ? getSlotOptionsForPageId(pageId) : []), [pageId]);

  const handlePageTypeChange = (value: string) => {
    setPageType(value);
    const ids = getPageIdsForPageType(value as PageType);
    const firstPageId = ids[0]?.value ?? '';
    setPageId(firstPageId);
    const slots = firstPageId ? getSlotOptionsForPageId(firstPageId) : [];
    setSlot(slots[0]?.value ?? '');
  };

  const handlePageIdChange = (value: string) => {
    setPageId(value);
    const slots = getSlotOptionsForPageId(value);
    setSlot(slots[0]?.value ?? '');
  };

  const customerIdTooLong = customerId.trim().length > VALIDATION.CUSTOMER_ID_MAX_LENGTH;
  const isValid =
    deviceId !== '' &&
    customerId.trim() !== '' &&
    creativeId.trim() !== '' &&
    pageType !== '' &&
    pageId !== '' &&
    slot !== '' &&
    !customerIdTooLong;

  const getFieldError = (value: string): string | undefined => {
    if (!hasSubmitted) return undefined;
    if (value.trim() === '') return VALIDATION.REQUIRED;
    return undefined;
  };

  const getCustomerIdError = (): string | undefined => {
    if (customerIdTooLong) return VALIDATION.CUSTOMER_ID_TOO_LONG;
    return getFieldError(customerId);
  };

  const getDropdownError = (value: string): string | undefined => {
    if (!hasSubmitted) return undefined;
    if (!value) return VALIDATION.REQUIRED;
    return undefined;
  };

  const resetForm = useCallback(() => {
    setDeviceId(defaultDeviceId);
    setCustomerId(deviceOptions.find((d) => d.value === defaultDeviceId)?.customerId ?? '');
    setCreativeId('');
    setDuration(DEFAULT_DURATION_SECONDS);
    const {
      pageType: pt,
      pageId: pid,
      slot: s,
    } = computeDefaultFormValues(defaults, defaultPageType);
    setPageType(pt);
    setPageId(pid);
    setSlot(s);
    setHasSubmitted(false);
  }, [defaults, defaultDeviceId, defaultPageType, deviceOptions]);

  // Reset form to latest defaults when modal opens (component stays mounted)
  useEffect(() => {
    if (isVisible) resetForm();
  }, [isVisible, resetForm]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    setHasSubmitted(true);
    if (!isValid) return;
    await onStartPreview({
      deviceId: isPhysicalDevice ? LABELS.PHYSICAL_DEVICE : deviceId,
      customerId: customerId.trim(),
      creativeId: creativeId.trim(),
      durationInSeconds: parseInt(duration, 10),
      pageType,
      pageId,
      slot,
    });
    resetForm();
  }, [
    isValid,
    isPhysicalDevice,
    deviceId,
    customerId,
    creativeId,
    duration,
    pageType,
    pageId,
    slot,
    onStartPreview,
    resetForm,
  ]);

  return (
    <KatModal visible={isVisible} onClose={handleClose} data-testid="device-preview-modal">
      <span slot="title">{LABELS.MODAL_TITLE}</span>
      <div className={styles.form}>
        {
          <KatDropdown
            label={LABELS.DEVICE}
            value={deviceId}
            options={[...allDeviceOptions]}
            onChange={(e: KatDropdown.ChangeEvent) => {
              const value = e.detail.value;
              setDeviceId(value);
              const selected = deviceOptions.find((d) => d.value === value);
              setCustomerId(selected?.customerId ?? '');
            }}
            placeholder={LABELS.SELECT_DEVICE}
            state={getDropdownError(deviceId) ? 'error' : undefined}
            stateLabel={getDropdownError(deviceId)}
            data-testid="device-select"
          />
        }
        <KatInput
          label={LABELS.CUSTOMER_ID}
          value={customerId}
          onInput={(e: KatInput.ChangeEvent) => setCustomerId(e.currentTarget.value)}
          state={getCustomerIdError() ? 'error' : undefined}
          stateLabel={getCustomerIdError()}
          data-testid="customer-id-input"
        />
        <KatInput
          label={LABELS.CREATIVE_ID}
          value={creativeId}
          onInput={(e: KatInput.ChangeEvent) => setCreativeId(e.currentTarget.value)}
          state={getFieldError(creativeId) ? 'error' : undefined}
          stateLabel={getFieldError(creativeId)}
          data-testid="creative-id-input"
        />
        <KatDropdown
          label={LABELS.PAGE_TYPE}
          value={pageType}
          options={[...pageTypeOptions]}
          onChange={(e: KatDropdown.ChangeEvent) => handlePageTypeChange(e.detail.value)}
          state={getDropdownError(pageType) ? 'error' : undefined}
          stateLabel={getDropdownError(pageType)}
          data-testid="page-type-select"
        />
        <KatDropdown
          label={LABELS.PAGE_ID}
          value={pageId}
          options={[...pageIdOptions]}
          onChange={(e: KatDropdown.ChangeEvent) => handlePageIdChange(e.detail.value)}
          disabled={!pageType || undefined}
          state={getDropdownError(pageId) ? 'error' : undefined}
          stateLabel={getDropdownError(pageId)}
          data-testid="page-id-select"
        />
        <KatDropdown
          label={LABELS.SLOT}
          value={slot}
          options={[...slotOptions]}
          onChange={(e: KatDropdown.ChangeEvent) => setSlot(e.detail.value)}
          disabled={!pageId || undefined}
          state={getDropdownError(slot) ? 'error' : undefined}
          stateLabel={getDropdownError(slot)}
          data-testid="slot-select"
        />
        <KatDropdown
          label={LABELS.DURATION}
          value={duration}
          options={[...DURATION_OPTIONS]}
          onChange={(e: KatDropdown.ChangeEvent) => setDuration(e.detail.value)}
          data-testid="duration-select"
        />
      </div>
      <div slot="footer" className={styles.footer}>
        <KatButton
          variant="secondary"
          label={LABELS.CANCEL}
          onClick={handleClose}
          data-testid="cancel-button"
        />
        <KatButton
          variant="primary"
          label={isLoading ? LABELS.STARTING : LABELS.START_PREVIEW}
          disabled={isLoading}
          onClick={handleSubmit}
          data-testid="start-preview-button"
        />
      </div>
    </KatModal>
  );
};
